import { useState, useEffect, useCallback, useRef } from 'react';
import { v2Api } from '@/lib/api';
import type { IDashboardSummary } from '@/types/dashboard';

export interface UseDashboardSummaryReturn {
  data: IDashboardSummary | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/** Loads relational dashboard metrics from ``GET /api/dashboard/summary/`` (no Mongo). */
export function useDashboardSummary(): UseDashboardSummaryReturn {
  const [data, setData] = useState<IDashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await v2Api.dashboard.summary.get();
      setData(res);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Failed to load dashboard summary.';
      setError(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      load();
    }
  }, [load]);

  return { data, loading, error, refetch: load };
}
